import { computed, inject, Injectable } from "@angular/core";

import { Store } from "@ngrx/store";

import { JsonConvert } from "json2typescript";

import {
    Basket,
    CustomOptions,
    PersistRequest,
} from "@hermes/api-model-basket";

// eslint-disable-next-line @typescript-eslint/naming-convention
import * as BasketActions from "../actions/basket.actions";
import {
    AddManyToCartPayload,
    AddToCartPayload,
    SerializedBasket,
} from "../models/basket-feature.model";
import { State } from "../reducers/basket.reducer";
import { basketQuery } from "../selectors/basket.selectors";

@Injectable()
export class BasketFacade {
    readonly #store = inject<Store<State>>(Store);

    /**
     * Selectors
     */
    public cart$ = this.#store.select(basketQuery.selectCart);
    public totalItems$ = this.#store.select(basketQuery.selectTotalItems);
    public billingAddressFromCart$ = this.#store.select(
        basketQuery.selectBillingAddress,
    );
    public error$ = this.#store.select(basketQuery.selectError);
    public hasErrors$ = this.#store.select(basketQuery.selectHasErrors);
    public pending$ = this.#store.select(basketQuery.selectPending);
    public taxInvoiceFromCart$ = this.#store.select(
        basketQuery.selectTaxInvoice,
    );
    public cfdiPropertiesFromCart$ = this.#store.select(
        basketQuery.selectCfdiProperties,
    );

    public readonly isInitialized = this.#store.selectSignal(
        basketQuery.selectIsInitialized,
    );

    public cart = this.#store.selectSignal(basketQuery.selectCart);
    public basket = this.#store.selectSignal(basketQuery.selectCart);

    public appointmentTimeslots = computed(
        () => this.basket().shippingAppointmentTimeslots ?? {},
    );

    public appointmentTimeslotDates = computed(() =>
        Object.keys(this.appointmentTimeslots()),
    );

    /*
     * Actions
     * @param addToCartPayload
     */
    public addToCart(addToCartPayload: AddToCartPayload): void {
        addToCartPayload.sendAnalyticsEvent =
            addToCartPayload.sendAnalyticsEvent ?? true;
        this.#store.dispatch(BasketActions.addToCart({ addToCartPayload }));
    }

    public addManyToCart(addManyToCartPayload: AddManyToCartPayload): void {
        this.#store.dispatch(
            BasketActions.addManyToCart({
                addManyToCartPayload,
            }),
        );
    }

    public removeFromCart(
        sku: string,
        qty: number,
        customOptions?: CustomOptions,
    ): void {
        this.#store.dispatch(
            BasketActions.removeFromCart({ sku, qty, customOptions }),
        );
    }

    public resetCart(): void {
        this.#store.dispatch(BasketActions.resetCart());
    }

    public setBasket(basket: Basket | SerializedBasket): void {
        this.#store.dispatch(
            BasketActions.setBasket({ basket: this.deserializeBasket(basket) }),
        );
    }

    public setCartItem(
        sku: string,
        qty: number,
        customOptions?: CustomOptions,
    ): void {
        this.#store.dispatch(
            BasketActions.setCartItem({ sku, qty, customOptions }),
        );
    }

    public persistBasket(persistRequest: PersistRequest): void {
        this.#store.dispatch(BasketActions.persistBasket({ persistRequest }));
    }

    public clearData(): void {
        this.#store.dispatch(BasketActions.clearData());
    }

    public deserializeBasket(basket: Basket | SerializedBasket): Basket {
        if (!("total_items" in basket)) {
            return basket;
        }

        return new JsonConvert().deserializeObject(basket, Basket);
    }
}
